/**
 * Seeds Table Configuration
 *
 * Column widths migrated to central registry (Day 2 - 2025-10-19)
 * Location: src/config/columns/columnRegistry.ts
 *
 * Default column orders and table configuration.
 * NO JSX - pure TypeScript only.
 *
 * Pattern matches:
 * - src/sections/asset/config/columns.ts
 * - src/sections/customerManagement/config/columns.ts
 */

export type SeedType = 'domain' | 'ip' | 'webapplication' | 'preseed';

// ============================================================================
// DEFAULT COLUMN ORDER
// ============================================================================

/**
 * Default column order for domain seeds
 */
export const SEED_DEFAULT_COLUMN_ORDER_DOMAIN = [
  'status',
  'identifier',
  'registrant',
  'origin',
  'created',
  'visited',
] as const;

/**
 * Default column order for IP seeds
 */
export const SEED_DEFAULT_COLUMN_ORDER_IP = [
  'status',
  'identifier',
  'registrant',
  'country',
  'origin',
  'created',
  'visited',
] as const;

/**
 * Default column order for webapp seeds
 */
export const SEED_DEFAULT_COLUMN_ORDER_WEBAPP = [
  'status',
  'identifier',
  'origin',
  'created',
  'visited',
] as const;

// ============================================================================
// TABLE CONFIGURATION
// ============================================================================

/**
 * Complete seed table configuration by type
 * Matches Assets table pattern
 *
 * Note: Column widths now in central registry (src/config/columns/columnRegistry.ts)
 * Note: This is separate from SEED_TABLE_CONFIG in constants.tsx
 * which contains pagination and sort configuration.
 */
export const SEED_TABLE_TYPE_CONFIG = {
  domain: {
    defaultColumnOrder: SEED_DEFAULT_COLUMN_ORDER_DOMAIN,
    storageKey: 'seeds-domain-v3',
    defaultRowHeight: 64, // Updated from 48 to match actual rendered height (browser cell padding adds ~16px)
  },
  ip: {
    defaultColumnOrder: SEED_DEFAULT_COLUMN_ORDER_IP,
    storageKey: 'seeds-ip-v3',
    defaultRowHeight: 64, // Updated from 48 to match actual rendered height (browser cell padding adds ~16px)
  },
  webapplication: {
    defaultColumnOrder: SEED_DEFAULT_COLUMN_ORDER_WEBAPP,
    storageKey: 'seeds-webapp-v3',
    defaultRowHeight: 64, // Updated from 48 to match actual rendered height (browser cell padding adds ~16px)
  },
} as const;

/**
 * Type-safe config getter
 *
 * @param type - Seed type ('domain', 'ip', 'webapplication', or 'preseed')
 * @returns Table configuration for the specified seed type
 */
export function getSeedTableConfig(type: SeedType) {
  // Preseed uses domain configuration
  if (type === 'preseed') {
    return SEED_TABLE_TYPE_CONFIG.domain;
  }
  return SEED_TABLE_TYPE_CONFIG[type] || SEED_TABLE_TYPE_CONFIG.domain;
}
